import type {
  CreativeDeploymentIdentity,
  CreativeDeploymentRef,
  PaidAdsProvider,
} from './contracts';

const DEPLOYMENT_KEY_VERSION = 'cfv-deployment-v1';
const ARTIFACT_SNAPSHOT_KEY_VERSION = 'cfv-artifact-snapshot-v1';

export interface CreativeArtifactSnapshotIdentity {
  projectId: string;
  provider: PaidAdsProvider;
  providerAccountId: string;
  providerResourceName: string;
  revisionRef: string;
}

export function buildCreativeDeploymentKey(identity: CreativeDeploymentIdentity): string {
  const parts = [
    identity.projectId,
    identity.provider,
    identity.providerAccountId,
    identity.decisionUnitKind,
    identity.providerResourceName,
  ].map((value, index) => requiredIdentityPart(value, index));
  return [DEPLOYMENT_KEY_VERSION, ...parts.map(encodeURIComponent)].join(':');
}

export function hasCanonicalDeploymentKey(deployment: CreativeDeploymentRef): boolean {
  return deployment.deploymentKey === buildCreativeDeploymentKey(deployment);
}

export function buildCreativeArtifactSnapshotKey(
  identity: CreativeArtifactSnapshotIdentity,
): string {
  const parts = [
    identity.projectId,
    identity.provider,
    identity.providerAccountId,
    identity.providerResourceName,
    identity.revisionRef,
  ].map((value, index) => requiredArtifactIdentityPart(value, index));
  return [ARTIFACT_SNAPSHOT_KEY_VERSION, ...parts.map(encodeURIComponent)].join(':');
}

function requiredIdentityPart(value: string, index: number): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    const labels = ['projectId', 'provider', 'providerAccountId', 'decisionUnitKind', 'providerResourceName'];
    throw new Error(`Creative deployment identity ${labels[index] ?? index} is required`);
  }
  return normalized;
}

function requiredArtifactIdentityPart(value: string, index: number): string {
  const normalized = String(value ?? '').trim();
  if (!normalized) {
    const labels = ['projectId', 'provider', 'providerAccountId', 'providerResourceName', 'revisionRef'];
    throw new Error(`Creative artifact snapshot identity ${labels[index] ?? index} is required`);
  }
  return normalized;
}
